import React from 'react';
import i18n from '@/i18n/config';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Autocomplete,
  AutocompleteItem,
  Chip,
  Spinner
} from '@heroui/react';
import { useAtom, useAtomValue } from 'jotai';
import { toast } from 'react-toastify';

import { permissionsQueryAtom, permissionsListParamsAtom, createRole, updateRole } from '@/store';
import { Permission, RoleItem } from '@/types';

interface RolePermissionsModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  role: RoleItem | null;
  onSuccess?: () => void;
}

export function RolePermissionsModal({ isOpen, onOpenChange, role, onSuccess }: RolePermissionsModalProps) {
  const [formData, setFormData] = React.useState({
    name: '',
    description: ''
  });
  const [permissions, setPermissions] = React.useState<number[]>([]);
  const [permissionSearch, setPermissionSearch] = React.useState('');
  const [permissionsParams, setPermissionsParams] = useAtom(permissionsListParamsAtom);
  const { data: permissionsData, isLoading: permissionsLoading } = useAtomValue(permissionsQueryAtom);
  const [, updateRoleAtom] = useAtom(updateRole);
  const [, createRoleAtom] = useAtom(createRole);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  // Cache initial permissions list
  const [initialPermissions, setInitialPermissions] = React.useState<Permission[]>([]);
  const [selectedKey, setSelectedKey] = React.useState<string | null>(null);

  const isEditMode = Boolean(role);

  // Initialize form data when role changes
  React.useEffect(() => {
    if (role) {
      setFormData({
        name: role.name.startsWith('admin_') ? role.name : `admin_${role.name}`,
        description: role.description || ''
      });
      setPermissions(role.permissions.map((p) => p.id));
    } else {
      setFormData({
        name: '',
        description: ''
      });
      setPermissions([]);
    }
  }, [role]);

  // Reset search when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setPermissionSearch('');
      setSelectedKey(null);
    }
  }, [isOpen]);

  // Cache initial permissions when first loaded
  React.useEffect(() => {
    if (permissionsData?.data && initialPermissions.length === 0) {
      setInitialPermissions(permissionsData.data);
    }
  }, [permissionsData, initialPermissions.length]);

  // Update permissions search filter with debounce
  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      const searchTerm = permissionSearch.trim();
      setPermissionsParams((prev) => ({
        ...prev,
        filters: {
          ...prev.filters,
          name: searchTerm || undefined
        }
      }));
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [permissionSearch, setPermissionsParams]);

  const availablePermissions = React.useMemo(() => {
    const result = permissionsData?.data ?? initialPermissions;
    return result;
  }, [permissionsData, initialPermissions, permissionSearch]);

  const handleClose = () => {
    // Only reset when closing, not when reopening
    setPermissionSearch('');
    setSelectedKey(null);
    onOpenChange(false);
  };

  const resetForm = () => {
    setFormData({ name: '', description: '' });
    setPermissions([]);
    setPermissionSearch('');
    setSelectedKey(null);
    setInitialPermissions([]); // Reset cache
  };

  const handleSubmit = React.useCallback(async () => {
    if (!formData.name.trim()) return;

    try {
      setIsSubmitting(true);

      // Ensure admin_ prefix is present before submitting
      const finalName = formData.name.startsWith('admin_') ? formData.name : `admin_${formData.name}`;

      if (isEditMode && role) {
        // Update existing role (name, description, and permissions)
        await updateRoleAtom({
          id: role.id,
          name: finalName,
          description: formData.description,
          permissions: permissions
        });
        toast.success(i18n.t('copy.role_updated_successfully'));
      } else {
        // Create new role
        await createRoleAtom({
          name: finalName,
          description: formData.description,
          permissions: permissions
        });
        toast.success(i18n.t('copy.role_created_successfully'));
      }

      onSuccess?.();
      resetForm(); // Reset form after successful submit
      handleClose();
    } catch (error) {
      console.error('Failed to save role:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, permissions, isEditMode, role, updateRoleAtom, createRoleAtom, onSuccess]);

  const handlePermissionSelect = (permission: Permission) => {
    if (!permissions.includes(permission.id)) {
      setPermissions((prev) => [...prev, permission.id]);
    }
    // Clear the search input and selection after selection
    setPermissionSearch('');
    setSelectedKey(null);
  };

  const handlePermissionRemove = (permissionId: number) => {
    setPermissions((prev) => prev.filter((id) => id !== permissionId));
  };

  const getPermissionName = (permissionId: number) => {
    // First try to find in current API data
    const currentPermission = permissionsData?.data?.find((p) => p.id === permissionId);
    if (currentPermission) {
      return currentPermission.name;
    }
    // Fallback to cached data
    const cachedPermission = initialPermissions.find((p) => p.id === permissionId);
    if (cachedPermission) {
      return cachedPermission.name;
    }
    return `Permission ${permissionId}`;
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange} size="lg" isDismissable={false}>
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              {isEditMode ? `Edit Role - ${role?.name}` : 'Create New Role'}
            </ModalHeader>
            <ModalBody>
              <div className="space-y-6">
                {/* Role Name */}
                <Input
                  label={i18n.t('copy.role_name')}
                  placeholder={i18n.t('copy.enter_role_name')}
                  value={formData.name}
                  onValueChange={(value) => {
                    // Simply set the value as is - no automatic prefix addition
                    setFormData((prev) => ({ ...prev, name: value }));
                  }}
                  isRequired
                />

                {/* Role Description */}
                <Input
                  label={i18n.t('copy.description')}
                  placeholder={i18n.t('copy.enter_role_description_optional')}
                  value={formData.description}
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, description: value }))}
                />

                {/* Permissions Autocomplete */}
                <div className="space-y-3">
                  <label className="text-sm font-medium">Add Permissions</label>

                  <Autocomplete
                    placeholder={i18n.t('copy.search_permissions')}
                    selectedKey={selectedKey}
                    inputValue={permissionSearch}
                    onInputChange={setPermissionSearch}
                    onSelectionChange={(key) => {
                      if (key && key !== null && key !== '') {
                        const permission = availablePermissions.find((p) => p.id === Number(key));
                        if (permission) {
                          handlePermissionSelect(permission);
                        }
                      }
                    }}
                    isLoading={permissionsLoading}
                    startContent={permissionsLoading ? <Spinner size="sm" /> : undefined}
                    allowsCustomValue={false}
                    menuTrigger="input"
                    shouldCloseOnBlur={false}
                  >
                    {availablePermissions.map((permission) => (
                      <AutocompleteItem key={permission.id.toString()} textValue={permission.name}>
                        {permission.description}
                      </AutocompleteItem>
                    ))}
                  </Autocomplete>

                  {/* Selected Permissions Chips */}
                  {permissions.length > 0 && (
                    <div className="space-y-2">
                      <label className="text-xs text-gray-500">Selected Permissions ({permissions.length}):</label>
                      <div className="max-h-48 overflow-y-auto rounded border border-gray-200 p-3 dark:border-gray-700">
                        <div className="flex flex-wrap gap-2">
                          {permissions.map((permissionId) => (
                            <Chip
                              key={permissionId}
                              color="primary"
                              variant="flat"
                              onClose={() => handlePermissionRemove(permissionId)}
                              className="cursor-pointer"
                            >
                              {getPermissionName(permissionId)}
                            </Chip>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button color="danger" variant="light" onPress={handleClose}>{i18n.t('modals.exportConfirm.cancel')}</Button>
              <Button
                color="primary"
                onPress={handleSubmit}
                isLoading={isSubmitting}
                isDisabled={!formData.name.trim()}
              >
                {isEditMode ? 'Update Role' : 'Create Role'}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
